import React, { useState, useRef, useCallback, useEffect, useMemo } from 'react';

import { DetailsSlide } from './slides/DetailsSlide';
import { ReferencesSlide } from './slides/ReferencesSlide';
import { SummarySlide } from './slides/SummarySlide';
import { SwipeableServiceCardProps, AllocationStatus } from './SwipeableServiceCard.props';
import {
  CardContainer,
  SlideContainer,
  SlideTrack,
  Slide,
  DotsContainer,
  Dot,
} from './SwipeableServiceCard.styles';

const getAllocationStatus = (
  primaryAllocationNode: string | undefined,
  currentUserName: string | undefined
): AllocationStatus => {
  if (!primaryAllocationNode) {
    return 'unallocated';
  }
  if (!currentUserName) {
    return 'otherUser';
  }
  if (primaryAllocationNode.toLowerCase() === currentUserName.toLowerCase()) {
    return 'currentUser';
  }
  return 'otherUser';
};

export const SwipeableServiceCard: React.FC<SwipeableServiceCardProps> = ({
  service,
  currentUserName,
  onClick,
}) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [startY, setStartY] = useState(0);
  const [translateX, setTranslateX] = useState(0);
  const directionLocked = useRef<'horizontal' | 'vertical' | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const trackRef = useRef<HTMLDivElement>(null);

  const DIRECTION_LOCK_THRESHOLD = 10;
  const SLIDE_CHANGE_THRESHOLD = 50;

  const allocationStatus = getAllocationStatus(service.primaryAllocationNode, currentUserName);
  const zone = service.serviceZone || service.locationZone;
  const totalSlides = 3;
  const SWIPE_THRESHOLD = 80;
  const TAP_THRESHOLD = 15;

  const applyTrackTransform = useCallback(
    (dx: number, animate: boolean) => {
      if (!trackRef.current || !containerRef.current) return;
      trackRef.current.style.transition = animate
        ? 'transform 0.35s cubic-bezier(0.32, 0.72, 0, 1)'
        : 'none';
      trackRef.current.style.transform = `translateX(${-currentSlide * containerRef.current.offsetWidth + dx}px)`;
    },
    [currentSlide]
  );

  const lockDirection = useCallback(
    (dx: number, dy: number): 'horizontal' | 'vertical' | null => {
      if (directionLocked.current) return directionLocked.current;
      if (Math.abs(dx) < DIRECTION_LOCK_THRESHOLD && Math.abs(dy) < DIRECTION_LOCK_THRESHOLD) return null;
      directionLocked.current = Math.abs(dx) > Math.abs(dy) ? 'horizontal' : 'vertical';
      return directionLocked.current;
    },
    []
  );

  const resolveSwipe = useCallback(() => {
    if (!isDragging || !containerRef.current) return;
    setIsDragging(false);
    const wasHorizontal = directionLocked.current === 'horizontal';
    directionLocked.current = null;

    if (wasHorizontal && Math.abs(translateX) > SLIDE_CHANGE_THRESHOLD) {
      const direction = translateX < 0 ? 1 : -1;
      setCurrentSlide((prev) => (prev + direction + totalSlides) % totalSlides);
    }
    applyTrackTransform(0, true);
    setTranslateX(0);
  }, [isDragging, translateX, totalSlides, applyTrackTransform]);

  const startDrag = useCallback((x: number, y: number) => {
    setStartX(x);
    setStartY(y);
    setIsDragging(true);
    setTranslateX(0);
    directionLocked.current = null;
  }, []);

  const moveDrag = useCallback(
    (x: number, y: number) => {
      if (!isDragging || !containerRef.current) return;
      const dx = x - startX;
      const dy = y - startY;
      const dir = lockDirection(dx, dy);
      if (!dir || dir === 'vertical') return;
      setTranslateX(dx);
      applyTrackTransform(dx, false);
    },
    [isDragging, startX, startY, lockDirection, applyTrackTransform]
  );

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    startDrag(e.touches[0].clientX, e.touches[0].clientY);
  }, [startDrag]);

  const handleTouchMove = useCallback(
    (e: React.TouchEvent) => moveDrag(e.touches[0].clientX, e.touches[0].clientY),
    [moveDrag]
  );

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    startDrag(e.clientX, e.clientY);
    e.preventDefault();
  }, [startDrag]);

  const handleMouseMove = useCallback(
    (e: React.MouseEvent) => moveDrag(e.clientX, e.clientY),
    [moveDrag]
  );

  // Update slide position when currentSlide changes
  useEffect(() => {
    if (!containerRef.current || !trackRef.current) return;
    trackRef.current.style.transform = `translateX(${-currentSlide * containerRef.current.offsetWidth}px)`;
  }, [currentSlide]);

  // Handle card click (only if not swiping)
  const handleCardClick = useCallback(() => {
    if (Math.abs(translateX) < TAP_THRESHOLD && onClick) {
      onClick();
    }
  }, [translateX, onClick]);

  // Only mount the active slide and its immediate neighbours for smooth
  // transitions. Avoids rendering all 3 slides * N cards.
  const shouldMount = useMemo(
    () =>
      Array.from({ length: totalSlides }, (_, i) => {
        const diff = Math.abs(currentSlide - i);
        return diff <= 1 || diff === totalSlides - 1;
      }),
    [currentSlide, totalSlides]
  );

  const slides = [
    <SummarySlide
      key="summary"
      service={service}
      allocationStatus={allocationStatus}
      zone={zone}
    />,
    <ReferencesSlide
      key="references"
      service={service}
      allocationStatus={allocationStatus}
      zone={zone}
    />,
    <DetailsSlide key="details" service={service} allocationStatus={allocationStatus} />,
  ];

  return (
    <CardContainer
      allocationStatus={allocationStatus}
      onClick={handleCardClick}
      data-testid="service-card"
      data-allocation={allocationStatus}
    >
      <SlideContainer
        ref={containerRef}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={resolveSwipe}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={resolveSwipe}
        onMouseLeave={resolveSwipe}
        data-testid="swipe-container"
      >
        <SlideTrack ref={trackRef}>
          {slides.map((slide, i) => (
            <Slide key={i}>{shouldMount[i] ? slide : null}</Slide>
          ))}
        </SlideTrack>
      </SlideContainer>
      <DotsContainer>
        {slides.map((_, i) => (
          <Dot key={i} active={currentSlide === i} data-dot={i + 1} />
        ))}
      </DotsContainer>
    </CardContainer>
  );
};
